import {
  type BoxRect,
  type GeometryResolver,
  type HighlightMode,
  type HighlightRange,
} from './types';

export interface AddHighlightOptions {
  range: HighlightRange;
  mode: HighlightMode;
  zIndex: number;
  className?: string;
  elementId?: string;
}

export class HighlightsRenderer {
  #elements = new Map<string, HTMLElement>();
  #activeRanges = new Map<string, { range: HighlightRange; mode: HighlightMode }>();

  #parentContainer: HTMLElement;
  #layerContainer: HTMLElement;
  #resolver: GeometryResolver;

  #rafId: number | null = null;
  #hidden = false;
  #disposed = false;

  constructor(container: HTMLElement, resolver: GeometryResolver) {
    this.#parentContainer = container;
    this.#resolver = resolver;

    this.#layerContainer = document.createElement('div');
    this.#layerContainer.style.position = 'absolute';
    this.#layerContainer.style.top = '0';
    this.#layerContainer.style.left = '0';
    this.#layerContainer.style.pointerEvents = 'none';

    this.#parentContainer.appendChild(this.#layerContainer);
  }

  add({ range, mode, zIndex, className, elementId }: AddHighlightOptions) {
    let el = this.#elements.get(range.id);

    if (!el) {
      el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.top = '0';
      el.style.left = '0';
      el.style.pointerEvents = 'none';

      this.#elements.set(range.id, el);
      this.#layerContainer.appendChild(el);
    }

    if (elementId !== undefined && el.id !== elementId) {
      el.id = elementId;
    }

    if (className && el.className !== className) {
      el.className = className;
    }

    el.style.zIndex = zIndex.toString();

    this.#activeRanges.set(range.id, { range, mode });

    // Synchronous update for instant feedback on new additions
    this.update(range, mode);
  }

  update(range: HighlightRange, mode: HighlightMode) {
    const el = this.#elements.get(range.id);

    if (!el) {
      return;
    }

    // Cancel any pending batched redraw to prevent stale bounds from overwriting
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);

      this.#rafId = null;
    }

    this.#activeRanges.set(range.id, { range, mode });

    const bounds = this.#measureBounds(range, mode);
    this.#applyBoundsToElement(el, bounds);
  }

  /**
   * Hides the entire layer. O(1) operation.
   * Use showAll() to restore visibility.
   */
  hideAll() {
    this.#hidden = true;
    this.#layerContainer.style.display = 'none';
  }

  /**
   * Shows the layer and redraws all highlights.
   */
  showAll() {
    this.#hidden = false;
    this.#layerContainer.style.display = '';
    this.redrawAll();
  }

  /**
   * Forces a recalculation of all active highlights.
   * CRITICAL for scroll and resize events.
   */
  redrawAll() {
    if (this.#hidden) {
      return;
    }

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
    }

    const pendingWrites: Array<{ el: HTMLElement; bounds: BoxRect | null }> = [];

    this.#activeRanges.forEach(({ range, mode }) => {
      const el = this.#elements.get(range.id);

      if (el) {
        pendingWrites.push({
          el,
          bounds: this.#measureBounds(range, mode),
        });
      }
    });

    this.#rafId = requestAnimationFrame(() => {
      pendingWrites.forEach(({ el, bounds }) => {
        this.#applyBoundsToElement(el, bounds);
      });

      this.#rafId = null;
    });
  }

  remove(id: string) {
    const el = this.#elements.get(id);

    if (el) {
      el.remove();

      this.#elements.delete(id);
      this.#activeRanges.delete(id);
    }
  }

  clear() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);

      this.#rafId = null;
    }

    this.#elements.forEach((el) => el.remove());
    this.#elements.clear();
    this.#activeRanges.clear();
  }

  dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;
    this.clear();
    this.#layerContainer.remove();
  }

  // ============================================================================
  // PRIVATE DOM HELPERS
  // ============================================================================

  #measureBounds(range: HighlightRange, mode: HighlightMode): BoxRect | null {
    const minRow = Math.min(range.anchor.row, range.lead.row);
    const maxRow = Math.max(range.anchor.row, range.lead.row);
    const minCol = Math.min(range.anchor.col, range.lead.col);
    const maxCol = Math.max(range.anchor.col, range.lead.col);

    if (mode === 'cell') {
      const topLeft = this.#resolver.getCellBounds(minRow, minCol);
      const bottomRight = this.#resolver.getCellBounds(maxRow, maxCol);

      if (topLeft && bottomRight) {
        return {
          x: topLeft.x,
          y: topLeft.y,
          width: bottomRight.x + bottomRight.width - topLeft.x,
          height: bottomRight.y + bottomRight.height - topLeft.y,
        };
      }
    } else if (mode === 'row') {
      const top = this.#resolver.getRowBounds(minRow);
      const bottom = this.#resolver.getRowBounds(maxRow);

      if (top && bottom) {
        return {
          x: top.x,
          y: top.y,
          width: top.width,
          height: bottom.y + bottom.height - top.y,
        };
      }
    } else if (mode === 'column') {
      const left = this.#resolver.getColumnBounds(minCol);
      const right = this.#resolver.getColumnBounds(maxCol);

      if (left && right) {
        return {
          x: left.x,
          y: left.y,
          width: right.x + right.width - left.x,
          height: left.height,
        };
      }
    } else if (mode === 'container') {
      return this.#resolver.getContainerBounds();
    }

    return null;
  }

  #applyBoundsToElement(el: HTMLElement, bounds: BoxRect | null) {
    if (this.#hidden) {
      return;
    }

    if (bounds) {
      el.style.transform = `translate(${bounds.x}px, ${bounds.y}px)`;
      el.style.width = `${bounds.width}px`;
      el.style.height = `${bounds.height}px`;
      el.style.display = 'block';
    } else {
      el.style.display = 'none';
    }
  }
}
